import { useMemo } from "react";
import { connect } from "react-redux";
import slugify from "slugify";
import { Center, CardsGrid, Flex } from "./styles";
import Grid from "../../../../../../components/Grid";
import CardAticle from "../../../components/CardArticle";
import PostCardSkeleton from "../../../components/PostCardSkeleton";
import NoPostFound from '../../components/NoPostFound'
import CategoryListHeader from '../CategoryListHeader'
import { Group } from "../../../../../../types";
import { Post } from "../../../../../../types/post";
import { SectionTitle } from "../../styles";
import EmptyCard from "../../../../components/EmptyCard";
import Carousel from "../../../../../../components/Carousel";
import postPinCheckedIcon from "../../../../../../assets/icons/post-pin-checked-icon.svg";
import { CategoryDto } from "../../../../../../types/category";
import { useWindowSize } from "../../../../../../hooks/useWindowSize";

type CustomCategoryDto = Omit<CategoryDto, 'id'> & { id: number | 'without_category' };

const groupTypes = {
  membership: "membresía",
  experience: "taller",
  free_experience: "evento",
};

interface ComponentProps {
  group: Group;
  groupStates: {
    loading: boolean;
    success: boolean;
    error: string | boolean;
  };
  allCategories: CategoryDto[];
  allCategoriesState: {
    loading: boolean;
    success: boolean;
    error: string | boolean;
  };
  setReorderContentSidebarVisible;
  setEditCategorySidebarVisible;
  setUnpinedItem;
  setIdCategoryToOrder;
  setCategoryToEdit;
  setIdArticleToDelete;
}

const Component = ({
  group,
  groupStates,
  allCategories,
  allCategoriesState,
  setReorderContentSidebarVisible,
  setEditCategorySidebarVisible,
  setUnpinedItem,
  setIdCategoryToOrder,
  setCategoryToEdit,
  setIdArticleToDelete
}: ComponentProps) => {
  const { isMobile } = useWindowSize()
  const thereAreCategories = useMemo(() => {
    return !!allCategories && allCategories?.length > 0
  }, [allCategories])

  const thereArePosts = useMemo(() => {
    const foundPosts = allCategories?.map((categories) => categories?.last_posts).flat()
    return foundPosts?.length > 0
  }, [allCategories])

  const shouldShowNoContentState = useMemo(() => {
    const noContent = !thereAreCategories && !thereArePosts;
    const notLoading = !allCategoriesState.loading;
    const hasError = allCategoriesState.error;

    return (noContent && notLoading) || (hasError && notLoading);
  }, [thereAreCategories, thereArePosts, allCategoriesState.loading, allCategoriesState.error]);

  const agregarOrdenDesbloqueo = (categories: CategoryDto[]): CustomCategoryDto[] & { unlock_order?: number } => {
    if (!categories) return []
    // Extraer y ordenar las categorías con days_to_unlock
    const ordered = [...categories]
      .filter(cat => typeof cat.days_to_unlock === 'number')
      .sort((a, b) => a.days_to_unlock - b.days_to_unlock);

    // Mapear id → orden
    const orderMap = new Map();
    ordered.forEach((cat, index) => {
      orderMap.set(cat.id, index + 1);
    });

    // Agregar unlock_order solo a las que tienen days_to_unlock
    return categories.map(cat =>
      orderMap.has(cat.id)
        ? { ...cat, unlock_order: orderMap.get(cat.id) }
        : cat
    );
  };

  return !!group ? (
    <>
      <Grid.Container>
        <Grid.Row>
          <Grid.Col>
            <SectionTitle>
              {group?.group_type === "community" ? "Tus Lead Magnets" : "Categorías de tu bibioteca"}
            </SectionTitle>
          </Grid.Col>
        </Grid.Row>
      </Grid.Container>

      <div style={{ height: 36 }} />

      {!thereAreCategories && !thereArePosts && allCategoriesState.loading && (
        <CardsGrid>
          <PostCardSkeleton />
          <PostCardSkeleton />
          <PostCardSkeleton />
        </CardsGrid>
      )}

      {shouldShowNoContentState ? (
        <EmptyCard
          title="No tienes artículos en la biblioteca"
          text={`Crea artículos para compartir con tus seguidores ${groupTypes[group?.group_type]
            }.`}
          icon={postPinCheckedIcon}
        />
      ) : (
        <>
          {allCategories?.map((category, index) => {
            return (
              <>
                <Grid.Container>
                  {group?.group_type !== "community" && (
                    <Grid.Row>
                      <Grid.Col>
                        <CategoryListHeader
                          category={category}
                          setCategoryToEdit={setCategoryToEdit}
                          setEditCategorySidebarVisible={setEditCategorySidebarVisible}
                          setIdCategoryToOrder={setIdCategoryToOrder}
                          setReorderContentSidebarVisible={setReorderContentSidebarVisible}
                        />
                      </Grid.Col>
                    </Grid.Row>
                  )}

                  <div style={{ height: 24 }} />

                  <Grid.Row>
                    <Grid.Col>
                      {category?.last_posts && category?.last_posts.length > 0 ? (
                        <Carousel
                          items={category?.last_posts}
                          renderItem={(post: Post, index: number) => (
                            <CardAticle
                              key={slugify(post.title + index, { lower: true })}
                              post={post}
                              onDelete={(idPost) => setIdArticleToDelete(idPost)}
                              groupInformation={group}
                              onUnpinIt={setUnpinedItem}
                            //blockedPost={!post?.user_access_data?.is_unlocked}
                            />
                          )}
                          slidesToShow={4}
                          scrollAmount={250}
                          animationSpeed={400}
                          showArrows={category?.last_posts?.length >= 4}
                          cardWidth="300px"
                        />
                      ) : (
                        <NoPostFound groupType={group?.group_type} />
                      )}
                    </Grid.Col>
                  </Grid.Row>

                  <div style={{ height: isMobile ? 46 : 34 }} />
                </Grid.Container>
              </>
            )
          })}
        </>
      )}
    </>
  ) : (
    <></>
  )
};

const state = ({ groupStore, categoryStore }) => {
  const { data: allCategories, states: allCategoriesState } = categoryStore.allCategories;
  const { data: group, states: groupStates } = groupStore.group;

  return {
    group,
    groupStates,
    allCategories,
    allCategoriesState,
  };
};

export default connect(state)(Component);
